import React, { useState, useEffect, createContext, useContext } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "./App.css";

// Import Pages
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import AdminDashboard from "./pages/AdminDashboard";
import SignatureManagement from "./pages/SignatureManagement";
import AdminManagement from "./pages/AdminManagement";
import SettingsPage from "./pages/SettingsPage";

// Import Components
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import PrivateRoute from "./components/common/PrivateRoute";
// import LoadingSpinner from "./components/common/LoadingSpinner";

// Create Auth Context
const AuthContext = createContext();

// Custom hook to use auth context
export const useAuth = () => useContext(AuthContext);

// Configure axios defaults
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
axios.defaults.baseURL = API_URL;

// Add request interceptor to include token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// ==================== API Service Functions ====================
const api = {
  // Auth
  auth: {
    login: async (username, password) => {
      const response = await axios.post("/auth/login", { username, password });
      return response.data;
    },
    getMe: async () => {
      const response = await axios.get("/auth/me");
      return response.data;
    },
  },

  // Signatures
  signatures: {
    create: async (data) => {
      const response = await axios.post("/signatures", data);
      return response.data;
    },
    getAll: async (params = {}) => {
      const response = await axios.get("/signatures", { params });
      return response.data;
    },
    updateStatus: async (id, status) => {
      const response = await axios.put(`/signatures/${id}/status`, { status });
      return response.data;
    },
    delete: async (id) => {
      const response = await axios.delete(`/signatures/${id}`);
      return response.data;
    },
  },

  // Statistics
  statistics: {
    track: async (page) => {
      const sessionId =
        sessionStorage.getItem("sessionId") ||
        `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem("sessionId", sessionId);

      const response = await axios.post(
        "/statistics/track",
        { page },
        {
          headers: { "X-Session-Id": sessionId },
        }
      );
      return response.data;
    },
    getDashboard: async () => {
      const response = await axios.get("/statistics/dashboard");
      return response.data;
    },
    getByPeriod: async (period) => {
      const response = await axios.get(`/statistics/${period}`);
      return response.data;
    },
  },

  // Admin
  admin: {
    getAll: async () => {
      const response = await axios.get("/admin/users");
      return response.data;
    },
    create: async (data) => {
      const response = await axios.post("/admin/users", data);
      return response.data;
    },
    update: async (id, data) => {
      const response = await axios.put(`/admin/users/${id}`, data);
      return response.data;
    },
    delete: async (id) => {
      const response = await axios.delete(`/admin/users/${id}`);
      return response.data;
    },
  },

  // Settings
  settings: {
    getAll: async () => {
      const response = await axios.get("/settings");
      return response.data;
    },
    update: async (data) => {
      const response = await axios.put("/settings", data);
      return response.data;
    },
  },
};

// ==================== Main App Component ====================
function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});

  // Check if user is logged in on mount
  useEffect(() => {
    const initApp = async () => {
      try {
        // Load settings
        const settingsRes = await api.settings.getAll();
        setSettings(settingsRes.data);

        // Check auth
        const token = localStorage.getItem("token");
        if (token) {
          const savedUser = localStorage.getItem("user");
          if (savedUser) {
            setUser(JSON.parse(savedUser));
          } else {
            const response = await api.auth.getMe();
            setUser(response.user);
            localStorage.setItem("user", JSON.stringify(response.user));
          }
        }

        // Track page view
        await api.statistics.track("home");
      } catch (error) {
        console.error("Init error:", error);
      } finally {
        setLoading(false);
      }
    };

    initApp();
  }, []);

  // Login function
  const login = async (username, password) => {
    try {
      const response = await api.auth.login(username, password);
      if (response.success) {
        localStorage.setItem("token", response.token);
        localStorage.setItem("user", JSON.stringify(response.user));
        setUser(response.user);

        // Show success message
        await Swal.fire({
          icon: "success",
          title: "เข้าสู่ระบบสำเร็จ",
          text: `ยินดีต้อนรับ ${response.user.fullname}`,
          showConfirmButton: false,
          timer: 1500,
        });

        return { success: true };
      }
    } catch (error) {
      const message =
        error.response?.data?.message || "เกิดข้อผิดพลาดในการเข้าสู่ระบบ";
      await Swal.fire({
        icon: "error",
        title: "เข้าสู่ระบบไม่สำเร็จ",
        text: message,
      });
      return { success: false, message };
    }
  };

  // Logout function
  const logout = async () => {
    const result = await Swal.fire({
      icon: "question",
      title: "ยืนยันการออกจากระบบ",
      text: "คุณต้องการออกจากระบบใช่หรือไม่?",
      showCancelButton: true,
      confirmButtonText: "ใช่, ออกจากระบบ",
      cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#007CC7",
      cancelButtonColor: "#6c757d",
    });

    if (result.isConfirmed) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setUser(null);

      await Swal.fire({
        icon: "success",
        title: "ออกจากระบบสำเร็จ",
        showConfirmButton: false,
        timer: 1500,
      });
    }
  };

  // Show toast notification
  const showToast = (message, type = "success") => {
    const Toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.addEventListener("mouseenter", Swal.stopTimer);
        toast.addEventListener("mouseleave", Swal.resumeTimer);
      },
    });

    Toast.fire({
      icon: type,
      title: message,
    });
  };

  const authValue = {
    user,
    login,
    logout,
    api,
    showToast,
    settings,
  };

  if (loading) {
    // return <LoadingSpinner />;
  }

  return (
    <AuthContext.Provider value={authValue}>
      <Router>
        <div className="app-container">
          <Header />
          <main className="main-content">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route
                path="/login"
                element={
                  user ? <Navigate to="/admin/dashboard" /> : <LoginPage />
                }
              />

              {/* Admin Routes */}
              <Route
                path="/admin"
                element={
                  <PrivateRoute>
                    <Navigate to="/admin/dashboard" />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/dashboard"
                element={
                  <PrivateRoute>
                    <AdminDashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/signatures"
                element={
                  <PrivateRoute>
                    <SignatureManagement />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <PrivateRoute requiredRole="super_admin">
                    <AdminManagement />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/settings"
                element={
                  <PrivateRoute>
                    <SettingsPage />
                  </PrivateRoute>
                }
              />

              {/* 404 */}
              <Route
                path="*"
                element={
                  <div className="container text-center py-5">
                    <h1 className="display-1">404</h1>
                    <p className="lead">ไม่พบหน้าที่คุณต้องการ</p>
                    <a href="/" className="btn btn-primary">
                      <i className="fas fa-home"></i> กลับหน้าหลัก
                    </a>
                  </div>
                }
              />
            </Routes>
          </main>
          <Footer />
        </div>
      </Router>
    </AuthContext.Provider>
  );
}

export default App;
